import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const charLimits = {
  designation: 100,
  areas_of_expertise: 500,
  about: 1000
};

const AdminEditExpertDialog = ({ open, onClose, expert, expertName, onSaved }) => {
  const [formData, setFormData] = useState({
    designation: '',
    areas_of_expertise: '',
    about: '',
    consultation_fee: 500
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Pre-fill form when dialog opens
  useEffect(() => {
    if (expert) {
      setFormData({
        designation: expert.designation || '',
        areas_of_expertise: expert.areas_of_expertise || '',
        about: expert.about || '',
        consultation_fee: expert.consultation_fee || 500
      });
      setErrors({});
      setSaveError('');
    }
  }, [expert, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required.';
    if (!formData.areas_of_expertise.trim()) newErrors.areas_of_expertise = 'Areas of expertise are required.';
    if (!formData.about.trim()) newErrors.about = 'About is required.';

    const fee = Number(formData.consultation_fee);
    if (isNaN(fee) || fee < 500 || fee > 2000) {
      newErrors.consultation_fee = 'Fee must be between ₹500 and ₹2000.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        user_id: expert.user_id,
        designation: formData.designation,
        areas_of_expertise: formData.areas_of_expertise,
        about: formData.about,
        consultation_fee: Number(formData.consultation_fee)
      };
      await axios.put(`${API_URL}/api/experts/update/`, payload);
      onSaved();
      onClose();
    } catch (err) {
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(145deg, #1a1a2e, #16213e)',
          border: '1px solid rgba(0, 191, 255, 0.2)',
          boxShadow: '0 0 40px rgba(0, 191, 255, 0.1)',
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon sx={{ color: '#00bfff' }} />
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
            Edit Expert Profile
          </Typography>
        </Box>
        {expertName && (
          <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5 }}>
            Editing: <strong style={{ color: '#00bfff' }}>{expertName}</strong>
          </Typography>
        )}
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(0,191,255,0.15)' }} />

      <DialogContent sx={{ pt: 2 }}>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

        <TextField
          name="designation"
          label="Designation"
          fullWidth
          required
          value={formData.designation}
          onChange={handleChange}
          error={!!errors.designation}
          helperText={errors.designation || `${formData.designation.length}/${charLimits.designation}`}
          inputProps={{ maxLength: charLimits.designation }}
          sx={inputSx}
        />

        <TextField
          name="areas_of_expertise"
          label="Areas of Expertise"
          fullWidth
          required
          multiline
          rows={3}
          value={formData.areas_of_expertise}
          onChange={handleChange}
          error={!!errors.areas_of_expertise}
          helperText={errors.areas_of_expertise || `${formData.areas_of_expertise.length}/${charLimits.areas_of_expertise}`}
          inputProps={{ maxLength: charLimits.areas_of_expertise }}
          sx={inputSx}
        />

        <TextField
          name="about"
          label="About"
          fullWidth
          required
          multiline
          rows={4}
          value={formData.about}
          onChange={handleChange}
          error={!!errors.about}
          helperText={errors.about || `${formData.about.length}/${charLimits.about}`}
          inputProps={{ maxLength: charLimits.about }}
          sx={inputSx}
        />

        <TextField
          name="consultation_fee"
          label="Consultation Fee (₹ per hour)"
          type="number"
          fullWidth
          required
          value={formData.consultation_fee}
          onChange={handleChange}
          onBlur={() => {
            let val = Number(formData.consultation_fee);
            if (!val || val < 500) val = 500;
            if (val > 2000) val = 2000;
            setFormData(prev => ({ ...prev, consultation_fee: val }));
          }}
          error={!!errors.consultation_fee}
          helperText={errors.consultation_fee || 'Allowed range: ₹500 – ₹2000'}
          inputProps={{ min: 500, max: 2000 }}
          sx={inputSx}
        />

        {/* Live fee breakdown */}
        {!errors.consultation_fee && Number(formData.consultation_fee) >= 500 && (
          <Typography variant="body2" sx={{ color: '#aaa', mt: 0.5, mb: 1 }}>
            30min: ₹{Number(formData.consultation_fee) / 2} &nbsp;|&nbsp;
            1hr: ₹{Number(formData.consultation_fee)} &nbsp;|&nbsp;
            1.5hr: ₹{Number(formData.consultation_fee) * 1.5} &nbsp;|&nbsp;
            2hr: ₹{Number(formData.consultation_fee) * 2}
          </Typography>
        )}
      </DialogContent>

      <Divider sx={{ borderColor: 'rgba(0,191,255,0.15)' }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ color: '#aaa', '&:hover': { color: '#fff' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: 'linear-gradient(45deg, #0077cc, #00bfff)',
            '&:hover': { background: 'linear-gradient(45deg, #005fa3, #009fd4)' },
            fontWeight: 700
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Shared dark-theme input styles
const inputSx = {
  mb: 2,
  backgroundColor: '#111827',
  borderRadius: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    color: '#fff',
    '& fieldset': { borderColor: '#374151' },
    '&:hover fieldset': { borderColor: '#00bfff' },
    '&.Mui-focused fieldset': { borderColor: '#00bfff', boxShadow: '0 0 8px rgba(0,191,255,0.25)' }
  },
  '& .MuiInputLabel-root': { color: '#9ca3af' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#00bfff' },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiFormHelperText-root': { color: '#6b7280' },
  '& .MuiFormHelperText-root.Mui-error': { color: '#f87171' }
};

export default AdminEditExpertDialog;